/** What the knowledge base API sends (REM-2, REM-3; docs/api.md). */

export type ArticleCategory = 'licensing' | 'payments' | 'games' | 'onboarding';

export const CATEGORY_LABELS: Record<ArticleCategory, string> = {
  licensing: 'Licensing',
  payments: 'Payments',
  games: 'Games',
  onboarding: 'Onboarding',
};

/** The bot's short answers stay short: about one Telegram line (REM-2). The API checks it too. */
export const SHORT_ANSWER_MAX = 300;

export type KBArticle = {
  id: string;
  title: string;
  category: ArticleCategory;
  short_answer: string;
  body: string;
  keywords: string[];
  created_by: string;
  demo: boolean;
  created_at: string;
  updated_at: string;
  times_used: number;
};

/** answers: the bot answers with it (the best match, sharing enough words). */
export type KBMatch = { article: KBArticle; score: number; answers: boolean };

/**
 * waiting: a draft for a person to confirm or correct. answered: the bot answered at once
 * on Telegram (Q19), and nobody on the team pressed 👍 or 👎 yet. removed: a team member's
 * 👎 took it out of the chat.
 */
export type AnswerStatus = 'waiting' | 'answered' | 'confirmed' | 'corrected' | 'removed';

export type BotAnswer = {
  id: string;
  question: string;
  channel: 'telegram' | 'email';
  lead_id: string;
  lead_email: string;
  answer: string;
  article_id: string;
  article_title: string;
  status: AnswerStatus;
  correction: string;
  decided_by: string;
  decided_at: string | null;
  /** The Telegram username of the team member who pressed 👍 or 👎. */
  decided_by_username: string;
  /** What the client pressed under an answer given at once. */
  client_feedback: '' | 'up' | 'down';
  demo: boolean;
  created_at: string;
  /** Telegram: the chat it replies in, and the client's message it answers. */
  chat_id: number | null;
  chat_title: string;
  reply_to_message_id: number | null;
  /** Email: where it goes. */
  email_to: string;
  email_subject: string;
  /** What was sent: at once, or once a person confirmed or corrected it. */
  sent_text: string;
  sent_at: string | null;
  /** The bot's message in the chat, with the buttons under it. */
  sent_message_id: number | null;
  /** Nothing really left Prisma: Telegram in simulate mode, or the log email sender. */
  simulated: boolean;
  /** Why the last send failed; "" once it went out. */
  send_error: string;
};

/** Where an answer goes, in words, or "" when it has nowhere to go (a demo row). */
export const destination = (a: BotAnswer) =>
  a.channel === 'telegram' && a.chat_id !== null
    ? a.chat_title || 'the Telegram chat'
    : a.channel === 'email' && a.email_to
      ? a.email_to
      : '';

/** What happened to the answer: waiting for a person, sent, simulated, failed, not sent, nothing to send, or removed from the chat. */
export type Delivery = 'waiting' | 'sent' | 'simulated' | 'failed' | 'unsent' | 'none' | 'removed';

export const delivery = (a: BotAnswer): Delivery => {
  if (a.status === 'waiting') return 'waiting';
  if (a.status === 'removed') return 'removed';
  if (a.sent_at) return a.simulated ? 'simulated' : 'sent';
  if (a.send_error) return 'failed';
  // A send cut off half-way (a closed tab, a restart) records nothing: it can go again.
  if (destination(a)) return 'unsent';
  return 'none';
};

/** Whether "Send again" is offered: the answer has somewhere to go and has not gone. */
export const resendable = (a: BotAnswer) => delivery(a) === 'failed' || delivery(a) === 'unsent';

/** Telegram lets the bot remove its own messages for 48 hours. */
export const removable = (a: BotAnswer) =>
  a.status === 'answered' && (!a.sent_at || Date.now() - new Date(a.sent_at).getTime() < 48 * 3600 * 1000);
